import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Outlet } from 'react-router-dom';
import { teamEventMetricFiltersAtom } from '@/atoms/queries/team/teamEventData';
import { selectedTeamNameAtom } from '@/atoms/team/info';
import { globalTeamIdAtom, teamIdAtom } from '@/atoms/team/team';
import { ContentState } from '@/components/contentState/ContentState';
import { TeamNav } from '@/components/navigation/subNav/TeamNav';
import { EntityTopBar } from '@/components/topBar/EntityTopBar';
import { layoutScopeAtoms } from '@/consts/scope';
import { useSetTeamComparisonDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetTeamComparisonDebouncedFilterParams';
import { useSetTeamDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetTeamDataDebounceFilterParams';
import { useSetTeamLeagueAverageDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetTeamLeagueAverageDebouncedFilterParams';
import { useAutoResetPagination } from '@/hooks/useAutoResetPagination';
import { useAutoResetSelectionLayer } from '@/hooks/useAutoResetSelectionLayer';
import { useSetMetricKey } from '@/hooks/useMetricKey';
import { useResetFiltersOnAreaEntry } from '@/hooks/useResetFiltersOnAreaEntry';
import { useSetIdAtom } from '@/hooks/useSetIdAtom';
import { useSetUserTeamSelectedAtom } from '@/hooks/useSetUserTeamSelectedAtom';
import { StatusTranslationKey } from '@/types/status';
import { AppShellContents } from '../AppShell';
import { RecordRecentVisit } from '../RecordRecentVisit';
import { SectionWrapper } from '../SectionWrapper';

const TeamLayoutInner = () => {
  const teamEventMetricFilters = useAtomValue(teamEventMetricFiltersAtom);
  useAutoResetSelectionLayer(teamEventMetricFilters);
  useAutoResetPagination(teamEventMetricFilters);

  return (
    <SectionWrapper section="team">
      <Outlet />
    </SectionWrapper>
  );
};

const TeamLayoutWithData = () => {
  useSetIdAtom(globalTeamIdAtom, 'teamId');
  useSetIdAtom(teamIdAtom, 'teamId');
  useSetUserTeamSelectedAtom();
  useSetMetricKey();
  const teamId = useAtomValue(teamIdAtom);

  return (
    <AppShellContents topBar={<EntityTopBar selectedEntityNameAtom={selectedTeamNameAtom} />} subNav={<TeamNav />}>
      <RecordRecentVisit type="team" />
      {teamId ? <TeamLayoutInner /> : <ContentState status={StatusTranslationKey.LOADING} />}
    </AppShellContents>
  );
};

export const SetTeamDataDebouncedFilterParams = () => {
  useSetTeamDataDebouncedFilterParams();
  useSetTeamComparisonDebouncedFilterParams();
  useSetTeamLeagueAverageDebouncedFilterParams();
  return null;
};

export const TeamLayout = () => {
  useResetFiltersOnAreaEntry();
  return (
    <ScopeProvider atoms={layoutScopeAtoms}>
      <SetTeamDataDebouncedFilterParams />
      <TeamLayoutWithData />
    </ScopeProvider>
  );
};
